<script setup>
  import {
    computed,
    onMounted,
    ref
  } from 'vue'

  import { useAjax } from '@composables/useAjax.js'
  import { useBookmarkProperty } from '@composables/useBookmarkProperty.js'
  import { useI18n } from '@shared/i18n.js'
  import { useRecaptcha } from '@composables/useRecaptcha.js'

  const {
    property_code: propertyCode
  } = window.app.page

  const ModalStatus = {
    CLOSED: 0,
    OPENING: 1,
    SHARED: 2,
  }

  const { callPost, errors } = useAjax()
  const { bookmark, getBookmark, unbookmark } = useBookmarkProperty()
  const { t, vt } = useI18n('properties.show.js.share')
  const { recaptchaToken } = useRecaptcha() // 問い合わせフォームのものを使用する

  const bookmarked = ref(false)
  const content = ref({ to_address: '', from_address: '', body: '' })
  const modalStatus = ref(ModalStatus.CLOSED)
  const submitting = ref(false)

  const urlFacebook = `https://www.facebook.com/share.php?u=${location.href}`
  // const urlFacebookMessenger = `fb-messenger://share/?link=${location.href}`
  const facebookAppId = document.querySelector("meta[property='fb:app_id']").getAttribute('content')
  const urlFacebookMessenger = `https://www.facebook.com/dialog/send?link=${location.href}&redirect_uri=${(new URL(window.location.href)).origin}&app_id=${facebookAppId}`
  const urlTwitter = `https://twiter.com/share?url=${location.href}`
  const urlWeChat = `https://www.addtoany.com/ext/wechat/share/#url=${location.href}`
  const urlWhatsapp = `https://wa.me/?text=${location.href}`

  const openingShare = computed(() => (modalStatus.value & ModalStatus.OPENING) > 0)
  const shared = computed(() => (modalStatus.value & ModalStatus.SHARED) > 0)
  const submittable = computed(() => content.value.from_address && content.value.to_address)

  onMounted(async () => {
    bookmarked.value = await getBookmark(propertyCode)
  })

  /**
   * お気に入りをクリック
   */
  const handleClickBookmark = async () => {
    if (submitting.value) return

    submitting.value = true
    try {
      if (bookmarked.value) {
        await unbookmark(propertyCode)
      }
      else {
        await bookmark(propertyCode)
      }
      bookmarked.value = !bookmarked.value
    }
    finally {
      submitting.value = false
    }
  }

  /**
   * シェア画面を開く
   */
  const handleClickShare = () => modalStatus.value = ModalStatus.OPENING

  /**
   * シェア画面を閉じる
   */
  const handleCloseShare = () => modalStatus.value = ModalStatus.CLOSED

  /**
   * シェア送信
   */
  const submitEmail = async () => {
    submitting.value = true
    try {
      const response = await callPost(window.app.page.share_path, { share_content: content.value, recaptcha_token: recaptchaToken.value })
      if (response && response.code == 'ok') {
        content.value = {}
        modalStatus.value |= ModalStatus.SHARED
      }
    }
    catch (e) {
      alert('Sorry something went wrong')
    }
    finally {
      submitting.value = false
    }
  }
</script>

<template>
  <div class="share_fav d-flex justify-content-between">
    <p class="share">
      <a class="modal-open" @click="handleClickShare">
        <i class="fa-regular fa-share-from-square"></i>
      </a>
    </p>

    <p class="fav">
      <a class="example2">
        <input :checked="bookmarked" type="checkbox" id="1">
        <label for="1"><i class="fa-solid fa-heart" @click="handleClickBookmark"></i></label>
      </a>
    </p>

    <div :class="{ active: openingShare }" class="modal-container">
      <div class="modal-body">
        <!-- 閉じるボタン -->
        <div class="modal-close" @click="handleCloseShare">×</div>
        <!-- モーダル内のコンテンツ -->
        <div class="modal-content">
          <template v-if="shared">
            <div style="font-weight: 700; padding: 1rem; text-align: center;">
              {{ vt('sent') }}
            </div>
          </template>
          <template v-else>
            <h3>Share home</h3>
            <dl>
              <dt>{{ t('activemodel.attributes.share_content.to_address') }}</dt>
              <dd>
                <input v-model="content.to_address" placeholder="Email*" type="text">
                <div v-if="errors.to_address" class="error_txt" v-html="errors.to_address.join('<br>')"></div>
              </dd>
            </dl>
            <dl>
              <dt>{{ t('activemodel.attributes.share_content.from_address') }}</dt>
              <dd>
                <input v-model="content.from_address" placeholder="Email*" type="text">
                <div v-if="errors.from_address" class="error_txt" v-html="errors.from_address.join('<br>')"></div>
              </dd>
            </dl>
            <dl>
              <dt>{{ t('activemodel.attributes.share_content.body') }}</dt>
              <dd>
                <textarea v-model="content.body" maxlength="2000" rows="3" :placeholder="vt('placeholder_message')"></textarea>
                <div v-if="errors.body" class="error_txt" v-html="errors.body.join('<br>')"></div>
              </dd>
            </dl>
            <button type="button" class="btn" @click="submitEmail">{{ vt('send_email') }}</button>
            <ul class="sns_list">
              <li><a :href="urlFacebook" rel="nofollow noopener" target="_blank"><i class="fa-brands fa-facebook-f"></i></a></li>
              <li><a :href="urlTwitter" rel="nofollow noopener" target="_blank"><i class="fa-brands fa-x-twitter"></i></a></li>
              <li><a :href="urlFacebookMessenger" target="_blank"><i class="fa-brands fa-facebook-messenger"></i></a></li>
              <li><a :href="urlWhatsapp" target="_blank"><i class="fa-brands fa-whatsapp"></i></a></li>
              <li><a :href="urlWeChat" target="_blank"><i class="fa-brands fa-weixin"></i></a></li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .error_txt {
    color: #ff0000;
    font-weight: 400;
  }
</style>
